import { useState, useCallback, useRef } from 'react';

interface UseUndoRedoOptions {
  /** Maximum number of states to keep in history */
  maxHistory?: number;
}

interface UseUndoRedoReturn<T> {
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Number of undo steps available */
  undoCount: number;
  /** Number of redo steps available */
  redoCount: number;
  /**
   * Record current state as a snapshot. Call this AFTER updating your state.
   * This creates a point you can undo back to.
   */
  snapshot: (state: T) => void;
  /**
   * Undo to previous state. Returns the state to restore, or null if nothing to undo.
   */
  undo: () => T | null;
  /**
   * Redo to next state. Returns the state to restore, or null if nothing to redo.
   */
  redo: () => T | null;
  /** Clear all history */
  clear: () => void;
  /** Get current history for debugging */
  getHistory: () => { states: T[]; index: number };
  /** Restore history from an external source (e.g. IndexedDB) */
  restore: (states: T[], index: number) => void;
}

const DEFAULT_MAX_HISTORY = 50;

/**
 * Deep equality check using JSON serialization
 */
function isEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Simple, robust undo/redo hook using index-based history.
 *
 * Usage:
 * ```tsx
 * const { canUndo, canRedo, snapshot, undo, redo } = useUndoRedo<MyState>();
 *
 * // When state changes, take a snapshot
 * function handleChange(newState: MyState) {
 *   setState(newState);
 *   snapshot(newState);
 * }
 *
 * // When user clicks undo
 * function handleUndo() {
 *   const previousState = undo();
 *   if (previousState) setState(previousState);
 * }
 * ```
 */
export function useUndoRedo<T>({
  maxHistory = DEFAULT_MAX_HISTORY,
}: UseUndoRedoOptions = {}): UseUndoRedoReturn<T> {
  // History is an array of states, with an index pointing to current position
  // history[0..index] are past states we can undo to
  // history[index+1..] are future states we can redo to (if any)
  const historyRef = useRef<T[]>([]);
  const indexRef = useRef<number>(-1); // -1 means no history yet

  // State to trigger re-renders when undo/redo availability changes
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  /**
   * Update the count states to reflect current history position
   */
  const updateCounts = useCallback(() => {
    const index = indexRef.current;
    const historyLength = historyRef.current.length;

    // Can undo if we're not at the beginning
    const newUndoCount = index >= 0 ? index : 0;
    // Can redo if there are states after current index
    const newRedoCount = historyLength > 0 ? historyLength - 1 - index : 0;

    setUndoCount(newUndoCount);
    setRedoCount(newRedoCount);
  }, []);

  /**
   * Take a snapshot of the current state. Call this AFTER updating your state.
   */
  const snapshot = useCallback(
    (state: T) => {
      const history = historyRef.current;
      const index = indexRef.current;

      // Don't add duplicate consecutive states
      if (index >= 0 && index < history.length && isEqual(history[index], state)) {
        return;
      }

      // If we're not at the end (user did some undos then made a change),
      // discard the "future" states
      if (index < history.length - 1) {
        historyRef.current = history.slice(0, index + 1);
      }

      // Add new state
      historyRef.current.push(state);

      // Trim if exceeds max (remove from beginning)
      if (historyRef.current.length > maxHistory) {
        historyRef.current = historyRef.current.slice(-maxHistory);
      }

      // Move index to the new state (end of array)
      indexRef.current = historyRef.current.length - 1;

      updateCounts();
    },
    [maxHistory, updateCounts],
  );

  /**
   * Undo to previous state
   */
  const undo = useCallback((): T | null => {
    const index = indexRef.current;

    // Can't undo if at beginning or no history
    if (index <= 0) {
      return null;
    }

    // Move index back
    indexRef.current = index - 1;

    updateCounts();

    // Return the state at the new index
    return historyRef.current[indexRef.current];
  }, [updateCounts]);

  /**
   * Redo to next state
   */
  const redo = useCallback((): T | null => {
    const history = historyRef.current;
    const index = indexRef.current;

    // Can't redo if at end or no history
    if (index >= history.length - 1) {
      return null;
    }

    // Move index forward
    indexRef.current = index + 1;

    updateCounts();

    // Return the state at the new index
    return historyRef.current[indexRef.current];
  }, [updateCounts]);

  /**
   * Clear all history
   */
  const clear = useCallback(() => {
    historyRef.current = [];
    indexRef.current = -1;
    setUndoCount(0);
    setRedoCount(0);
  }, []);

  /**
   * Get history for debugging
   */
  const getHistory = useCallback(
    () => ({
      states: [...historyRef.current],
      index: indexRef.current,
    }),
    [],
  );

  /**
   * Restore history from an external source (e.g. IndexedDB).
   * Replaces current in-memory refs entirely.
   */
  const restore = useCallback(
    (states: T[], index: number) => {
      historyRef.current = [...states];
      indexRef.current = Math.min(Math.max(index, -1), states.length - 1);
      updateCounts();
    },
    [updateCounts],
  );

  return {
    canUndo: undoCount > 0,
    canRedo: redoCount > 0,
    undoCount,
    redoCount,
    snapshot,
    undo,
    redo,
    clear,
    getHistory,
    restore,
  };
}
